<template>
    <div>
        <b-row role="alert">
            <b-col>
                <div class="sr-only hidden" v-if="isLoading">Licenses Loading, Please Wait...</div>
            </b-col>
        </b-row>
        <b-table hover small responsive show-empty class="sr-caption"
            aria-live="polite"
            :id="slug"
            :fields="fields"
            :items="items"
            primary-key="id"
            :busy="isLoading"
            label-sort-asc=""
            label-sort-desc=""
            caption="Licenses table.  To sort the data in this table by a specific column,
                   go to the appropriate column header cell and use enter to sort the data ascending.
                   Use enter again to sort descending.">
            <template v-slot:cell(actions)="row">
                <b-button size="sm" @click="launchPayment(row.item)" 
                    variant="warning"
                    class="mr-1 mt-1"
                    v-if="isPaymentPending(row.item)">
                Pay
                </b-button>
                <b-button size="sm" @click="launchRenewal(row.item)" 
                    variant="warning"
                    class="mr-1 mt-1"
                    v-if="isInRenewalPeriod(row.item)">
                Renew
                </b-button>
                <b-button size="sm" @click="launchReclassification(row.item)"
                          variant="warning"
                          class="mr-1 mt-1"
                          v-if="row.item.licenseSubCategory === 'TEMPORARY' &&
                          (row.item.status === 'ACTIVE' || row.item.status === 'EXPIRED')
                          && !row.item.isReclassified">
                    Apply for reclassification to full license
                </b-button>
                <b-button size="sm" @click="showWithdrawModal(row.item)"
                          variant="warning"
                          class="mr-1 mt-1"
                          v-if="isPending(row.item)">
                    Withdraw
                </b-button>
                <b-button size="sm" to="application" role="button"
                          variant="primary"
                          class="mr-1 mt-1"
                          v-if="isAfterRenewalPeriod(row.item)">
                    Apply
                </b-button>
                <b-button size="sm" @click="printCertificate(row.item)"
                          variant="primary"
                          class="mr-1 mt-1"
                          v-if="row.item.status === 'ACTIVE' || row.item.status === 'AMENDED'">
                    Print Certificate
                </b-button>
                <b-button size="sm" @click="launchReopen(row.item)"
                          variant="warning"
                          class="mr-1 mt-1"
                          v-if="row.item.returned && row.item.applicationStatus !== 'EXPIRED'">
                    Edit Application
                </b-button>
                <b-button size="sm" @click="printApplication(row.item)"
                          variant="primary"
                          class="mr-1 mt-1"
                          v-if="row.item.applicationId">
                    Print Application
                </b-button>
                <b-button size="sm" @click="viewHistory(row.item)"
                          variant="outline-primary"
                          class="mr-1 mt-1"
                          v-if="row.item.id">
                    View License History
                </b-button>
                <b-button size="sm" @click="viewMessage(row.item)" role="button"
                          variant="outline-primary"
                          class="mr-1 mt-1"
                          v-if="isPending(row.item)">
                    View Messages
                </b-button>
                <b-button size="sm" @click="launchWorkDates(row.item)"
                          variant="primary"
                          class="mr-1 mt-1"
                          v-if="(row.item.status == 'ACTIVE' || row.item.status === 'PENDING SUPERVISOR' || row.item.status === 'AMENDED')
                          && row.item.guestWorkDay != null && row.item.guestWorkDay < 30 && row.item.licenseSubCategory === 'GUEST'">
                    Adjust Work Dates
                </b-button>
                <b-button size="sm" @click="launchSupervisor(row.item)"
                          variant="primary"
                          class="mr-1 mt-1"
                          v-if="(row.item.status == 'ACTIVE' || row.item.status === 'PENDING SUPERVISOR' || row.item.status === 'AMENDED') && row.item.licenseSubCategory !== 'FULL'
                          && (row.item.licenseSubCategory !== 'GUEST' || (!!row.item.workDates && row.item.workDates.length > 0 ))">
                    Update {{row.item.licenseSubCategory === 'GUEST' ? "Sponsor" : "Supervisor"}}
                </b-button>
                <b-button size="sm" @click="launchEstablishment(row.item)"
                          variant="primary"
                          class="mr-1 mt-1"
                          v-if="(row.item.status == 'ACTIVE' || row.item.status === 'PENDING SUPERVISOR' || row.item.status === 'AMENDED') && row.item.licenseSubCategory !== 'FULL'
                          && (row.item.licenseSubCategory !== 'GUEST' || (!!row.item.workDates && row.item.workDates.length > 0))">
                    Update Establishment
                </b-button>
            </template>
            <template v-slot:cell(pendingApplication)="data">
                {{ data.item.pendingApplication ? "Yes" : "No" }}
            </template>

            <template v-slot:cell(workDates)="row">
                <p class="mt-0 mb-1" v-for="(date, index) in row.item.workDates" :key="index">
                    {{date}}
                </p>
            </template>

            <template v-slot:cell(formattedSupervisors)="row">
                <p class="mt-0 mb-1" v-for="(supervisor, index) in row.item.formattedSupervisors" :key="index">
                    {{supervisor}}
                </p>
            </template>
            
            <template v-slot:no-data>
                <v-btn color="primary" @click="initialize">Reset</v-btn>
            </template>

            <template v-slot:empty="scope">
                <p>No applications found for this user.</p>
            </template>

            <div slot="table-busy" class="text-center text-danger my-2">
                <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"
                           label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                <strong>Loading, Please Wait...</strong>
            </div>  
        </b-table>
        <b-modal id="withdraw-modal" hide-header hide-footer>
            <h2>Withdraw Application</h2>
            <hr/>
            <div>
                To withdraw your application, contact MDH through the messaging page. Please specify if you have already
                made a payment on this application which needs to be refunded.
                <div class="mt-1">
                    <b-button @click="closeWithdrawModal" style="margin-right:15px;" variant="secondary">Close</b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import moment from 'moment'
import axios from "axios"
import axiosRetry from 'axios-retry'
import * as FileSaver from 'file-saver';

axiosRetry(axios, { retries: 3,
    retryDelay: axiosRetry.exponentialDelay  })

export default {
    components: {
    },
    props: {
        items: Array,
        slug: [String],
        isLoading: Boolean,
    },
    data: () => ({
        fields: [
            {key: 'number', sortable: true, label: 'License #'},
            {key: 'formattedStatus', sortable: true, label: 'License Status'},
            {key: 'effectivePeriod', sortable: true, label: 'Dates Effective'},
            {key: 'formattedCategory', sortable: true, label: 'License Category'},
            {key: 'licenseType.description', sortable: true, label: 'License Type'},
            {key: 'pendingApplication', sortable: true, label: 'Pending Application', class: 'paCol'},
            {key: 'actions', label: 'Actions'}
        ],
    }),

    methods: {
        launchWorkDates(item) {
            this.$router.push({ name: 'addWorkDates', params: { id: item.applicationId } })
        },

        launchSupervisor(item) {
            this.$router.push({ name: 'addSupervisor', params: { id: item.applicationId } })
        },

        launchEstablishment(item) {
            this.$router.push({ name: 'addEstablisment', params: { id: item.applicationId } })
        },

        launchRenewal(item) {
            this.$router.push({ name: 'renewal', params: { token: item.id } })
        },

        launchReclassification(item) {
            this.$router.push({ name: 'reclassify', params: { id: item.id } })
        },

        launchReopen(item) {
            if (item.applicationType.code === 'R') {
                this.launchRenewalEdit(item)
            } else {
                this.launchEdit(item)
            }
        },

        launchPayment(item) {
            this.$router.push({ name: 'pay', params: { id: item.id } })
        },

        launchEdit(item) {
            this.$router.push({ name: 'editApplication', params: { id: item.applicationId } })
        },

        launchRenewalEdit(item) {
            this.$router.push({ name: 'editRenewal', params: { id: item.applicationId } })
        },

        printCertificate(item) {
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/certificate/generate/${item.id}`)
                .then(response => {
                    const file = response.data;
                    if (file.contentType == null) {
                        return;
                    }

                    const byteString = atob(file.fileData);
                    const arrayBuffer = new ArrayBuffer(byteString.length);
                    const int8Array = new Uint8Array(arrayBuffer);
                    for (let i = 0; i < byteString.length; i++) {
                        int8Array[i] = byteString.charCodeAt(i);
                    }
                    const blob = new Blob([int8Array], {type: file.contentType});
                    FileSaver.saveAs(blob, file.name);
                });
        },

        printApplication(item) {
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/pdf/application/${item.applicationId}`)
                .then(response => {
                    const file = response.data;
                    if (file.contentType == null) {
                        return;
                    }

                    const byteString = atob(file.fileData);
                    const arrayBuffer = new ArrayBuffer(byteString.length);
                    const int8Array = new Uint8Array(arrayBuffer);
                    for (let i = 0; i < byteString.length; i++) {
                        int8Array[i] = byteString.charCodeAt(i);
                    }
                    const blob = new Blob([int8Array], {type: file.contentType});
                    FileSaver.saveAs(blob, file.name);
                });
        },

        isInRenewalPeriod(item) {
            return item.renewalPeriod.inRenewalPeriod && item.resubmittable
                && ((item.status === "ACTIVE" || item.status === 'EXPIRED') && item.applicationStatus === 'APPROVED')
        },

        isAfterRenewalPeriod(item) {
            if (item.expirationDate) {
                let renewExpire = moment().subtract(6,'years').startOf('day')
                let expire = moment(item.expirationDate, 'YYYY-MM-DD').startOf('day')
                return expire < renewExpire
            } else {
                return false
            }
        },

        viewHistory(item) {
            this.$router.push({ name: 'history', params: { id: item.id } })
        },

        viewMessage(item) {
            this.$router.push({ name: 'message', params: { id: item.applicationId } })
        },

        isPaymentPending(item) {
            return item.paymentStatus === 'PENDING'
        },

        isPending(item) {
            return item.status === "PENDING" || item.pendingApplication
        },

        isActiveOrPending(item) {
            return item.status === "PENDING" || item.status === 'ACTIVE' || item.status === 'PENDING SUPERVISOR'
        },

        showWithdrawModal() {
            this.$bvModal.show('withdraw-modal')
        },

        closeWithdrawModal() {
            this.$bvModal.hide('withdraw-modal')
        },
    },
    mounted() {
        if(this.items.length > 0 && this.items.some(i => i.licenseSubCategory === "GUEST")
            && !this.fields.some(f => f.key === "workDates")) {
            this.fields.splice(this.fields.length -1, 0,
                {key: "workDates", label: "Work Dates"},
            )
        }
        if(this.items.length > 0 && this.items.some(i => i.licenseSubCategory === "TEMPORARY")
            && !this.fields.some(f => f.key === "formattedSupervisors")) {
            this.fields.splice(this.fields.length -1, 0,
                {key: "formattedSupervisors", label: "Supervised Dates"},
            )
        }
    }
}
</script>
<style>
.paCol { width: 15%; }
</style>